import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, PLATFORM_ID, SimpleChanges, ViewChild, inject, signal } from '@angular/core';
import { MultiMediaDTO, UserAccountDTO, XploreItemDTO } from "../../../shared/object/xplorer-be-parsed-classes";
import { environment } from "../../../../environments/environment";
import { ImageManager } from "../../../shared/image-manager";
import KeenSlider, { KeenSliderInstance } from "keen-slider";
import { NgOptimizedImage, isPlatformBrowser } from '@angular/common';
import { createFeedSliderOptions } from '../../../shared/keen-slider-options';

@Component({
    selector: 'app-mobile-item-card',
    templateUrl: './mobile-item-card.component.html',
    styleUrls: ['./mobile-item-card.component.css'],
    imports: [NgOptimizedImage]
})
export class MobileItemCardComponent implements AfterViewInit, OnChanges, OnDestroy {

  @ViewChild("sliderHorizRef")
  sliderRef?: ElementRef<HTMLElement>;

  @Input() xploreItem: XploreItemDTO;
  @Input() isPrimaryMedia: boolean = false;

  @Output() userProfileClickedEvent: EventEmitter<UserAccountDTO> = new EventEmitter<UserAccountDTO>();

  slider: KeenSliderInstance | null = null;
  readonly currentMediaIndex = signal(0);

  protected readonly ImageManager = ImageManager;
  private sliderSyncTimer?: ReturnType<typeof setTimeout>;
  private viewInitialized = false;
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private sliderTeardownTimer?: ReturnType<typeof setTimeout>;
  /** The vertical feed snap animation runs ~500ms; tearing down after it ends
      keeps the visible card from snapping back to its first image mid-transition. */
  private static readonly SLIDER_TEARDOWN_DELAY_MS = 700;

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.syncSlider();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['xploreItem'] || changes['isPrimaryMedia']) {
      this.syncSlider();
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.sliderSyncTimer);
    clearTimeout(this.sliderTeardownTimer);
    this.destroySlider();
  }

  getImage(image: MultiMediaDTO) {
    return image?.mediaURL ? environment.IMAGES_URL + image.mediaURL : ImageManager.getPlaceholderImage();
  }

  trackByMedia(index: number, media: MultiMediaDTO): string | number {
    return media?.mediaURL ?? index;
  }

  goToUserProfile(userAccount: UserAccountDTO) {
    this.userProfileClickedEvent.emit(userAccount);
  }

  onImageError($event: ErrorEvent) {
    const imgElement = $event.target as HTMLImageElement;
    imgElement.src = ImageManager.getPlaceholderImage();
    imgElement.srcset = '';
  }

  /**
   * The horizontal slider only runs on the active card: offscreen cards fall
   * back to the CSS slide sizing and show their first image. Teardown when the
   * card loses isPrimaryMedia is deferred (see scheduleSliderTeardown) so the
   * still-visible card doesn't visibly snap back to its first image mid vertical
   * transition.
   */
  private syncSlider(): void {
    if (!this.isBrowser || !this.viewInitialized) {
      return;
    }

    clearTimeout(this.sliderSyncTimer);
    // Defer one tick so the @for slides are in the DOM before keen measures them.
    this.sliderSyncTimer = setTimeout(() => {
      const mediaCount = this.xploreItem?.multiMedia?.length ?? 0;
      const sliderElement = this.sliderRef?.nativeElement;
      if (!this.isPrimaryMedia || mediaCount <= 1 || !sliderElement) {
        this.scheduleSliderTeardown();
        return;
      }

      clearTimeout(this.sliderTeardownTimer);
      if (this.slider) {
        this.slider.update(undefined, 0);
        this.currentMediaIndex.set(0);
      } else {
        this.slider = new KeenSlider(sliderElement, createFeedSliderOptions({
          slideChanged: (slider) => {
            const rel = slider.track.details?.rel;
            if (typeof rel === 'number') {
              this.currentMediaIndex.set(rel);
            }
          },
        }));
      }
    }, 0);
  }

  private scheduleSliderTeardown(): void {
    clearTimeout(this.sliderTeardownTimer);
    if (!this.slider) {
      return;
    }
    this.sliderTeardownTimer = setTimeout(() => this.destroySlider(), MobileItemCardComponent.SLIDER_TEARDOWN_DELAY_MS);
  }

  private destroySlider(): void {
    this.slider?.destroy();
    this.slider = null;
    this.currentMediaIndex.set(0);
  }
}
